import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getLogUserRequest, logout } from "../../../redux/action";
import { toast } from "react-toastify";
import { BottomNav } from "../../../components/Nav/BottomNav";
import CurrencyDisplay from "../../../components/Currency/CurrencyDisplay";
import { SideNav } from "../../../components/Nav/SideNav";

const Setting = () => {
  const dispatch = useDispatch();

  const { user, getLogUserError, token, getLogUserLoading } = useSelector(
    (state) => {
      const getLogUserError = state?.ajaxStatuses?.errors?.getLogUser;
      const getLogUserLoading = state?.loadingIndicator?.getLogUserLoading;
      const user = state?.userData?.user;
      const token = state?.userData?.token;

      return { user, getLogUserError, token, getLogUserLoading };
    },
  );

  useEffect(() => {
    if (getLogUserError) {
      toast.error(getLogUserError, { duration: 3000 });
    }
  }, [getLogUserError]);

  useEffect(() => {
    if (token) {
      dispatch(getLogUserRequest(token));
    }
  }, [dispatch, token]);

  const logOut = () => {
    dispatch(logout());
  };

  const handleCopyWallet = () => {
    if (user?.walletAddress) {
      navigator.clipboard.writeText(user.walletAddress);
      toast.success("Wallet address copied!", {
        duration: 2000,
        position: "top-center",
      });
    }
  };

  return (
    /* Locked to w-[428px] to replicate the mobile app view exactly. */
    <div className="w-[428px] mx-auto bg-[#f4f7ff] min-h-screen relative font-sans text-slate-900 pb-[100px] shadow-[0_0_50px_rgba(0,0,0,0.1)] overflow-x-hidden overflow-y-auto">
      {/* --- Top Blue Header --- */}
      <div
        className="absolute top-0 left-0 w-full h-[240px] bg-[#09267d] z-0 overflow-hidden rounded-b-3xl bg-no-repeat bg-right-top"
        style={{
          backgroundImage: "url('/img/new/background/africa-map.jpeg')",
          backgroundSize: "contain",
        }}
      >
        {/* Top Action Row */}
        <div className="relative z-10 px-6 pt-12 pb-4 flex justify-between items-center">
          <h1 className="text-[28px] font-semibold text-white ">Profile</h1>
          <button
            onClick={logOut}
            className="flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white px-3.5 py-1.5 rounded-lg text-[13px] font-semibold transition-colors active:scale-95 shadow-sm"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            Logout
          </button>
        </div>
      </div>

      {/* --- Main Content Area --- */}
      <div className="relative z-10 mt-[150px] px-4 flex flex-col gap-4">
        {getLogUserLoading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3 text-white">
            <svg
              className="animate-spin h-8 w-8 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="text-[13px] font-medium">Loading Profile...</p>
          </div>
        ) : (
          <>
            {/* --- User Profile Card (Overlapping header) --- */}
            <div className="bg-white rounded-[1.5rem] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-50 flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-[#f0f4ff] text-[#1a4bdf] rounded-full flex items-center justify-center text-[24px] font-black mb-3 shadow-md shadow-blue-600/10 border border-[#dce5fc]">
                {user?.userName ? user.userName.charAt(0).toUpperCase() : "U"}
              </div>

              <h2 className="text-[20px] font-semibold text-slate-900 mb-1">
                {user?.userName || "User"}
              </h2>

              <div className="bg-[#f8faff] border border-[#dce5fc] px-3 py-1 rounded-full text-[11px] font-medium text-slate-500 mb-4">
                {user?.email || "No email provided"}
              </div>

              {/* Wallet Address Display */}
              {user?.walletAddress && (
                <div
                  onClick={handleCopyWallet}
                  className="flex items-center gap-2 text-[12px] font-mono font-medium text-slate-500 bg-gray-50 hover:bg-blue-50 hover:text-[#1a4bdf] px-3 py-2 rounded-lg cursor-pointer transition-colors border border-gray-100"
                >
                  <span className="truncate w-[120px]">
                    {user.walletAddress.slice(0, 6)}...
                    {user.walletAddress.slice(-6)}
                  </span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="9"
                      y="9"
                      width="13"
                      height="13"
                      rx="2"
                      ry="2"
                    ></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </div>
              )}

              {/* --- Stats Row --- */}
              <div className="grid grid-cols-2 gap-0 border-t border-gray-200">
                <div className="bg-white p-5 flex flex-col items-center justify-center text-center gap-1">
                  <span className="text-[10px] uppercase font-semibold text-slate-400 tracking-widest">
                    Total Balance
                  </span>
                  <span className="text-[20px] font-black text-[#1a4bdf] tracking-tight">
                    <CurrencyDisplay
                      usdAmount={user?.balance || 0}
                      showCurrency={true}
                    />
                  </span>
                </div>

                <div className="bg-white p-5 border-l border-gray-200 flex flex-col items-center justify-center text-center gap-1">
                  <span className="text-[10px] uppercase font-semibold text-slate-400 tracking-widest">
                    Referrals
                  </span>
                  <span className="text-[20px] font-black text-slate-800 tracking-tight">
                    {user?.referralCount || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* --- Navigation Menu --- */}
            <div className="mt-2">
              <span className="block text-[13px] font-semibold text-slate-900 mb-3 ml-2">
                Account Management
              </span>
              <SideNav />
            </div>
          </>
        )}
      </div>

      {/* Include your bottom navigation component here if required */}
      <div className="fixed bottom-0 w-[428px] z-40 bg-white">
        <BottomNav />
      </div>
    </div>
  );
};

export default Setting;
